import { inject } from '@angular/core';
import {
  CanActivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
  CanMatchFn,
  UrlSegment,
  Route,
  Router
} from '@angular/router';
import {
  CustomNavigationService,
  NotificationService,
  SessionService,
} from '@kimbocare/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { UserSubscription } from '../models/Subscription';
import { SubscriptionService } from '../rest/usecases/subscription.service';

export function HasNotSubsGuardWithRedirection(): CanActivateFn {
  return (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree => {
    const authService: SessionService = inject(SessionService);

    const navService: CustomNavigationService = inject(CustomNavigationService);
    const subs = authService.getSubscription() as UserSubscription;
    if (
      !subs?.product ||
      (subs?.product &&
        subs.product.ends_at &&
        new Date(subs.product.ends_at) <= new Date())
    ) {
      return true;
    }
    navService.goTo('/dashboard');

    return false;
  };
}

export function HasNotSubsGuard(): CanActivateFn {
  return (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree => {
    const authService: SessionService = inject(SessionService);
    const subs = authService.getSubscription() as UserSubscription;
    if (
      !subs?.product ||
      (subs?.product &&
        subs.product.ends_at &&
        new Date(subs.product.ends_at) <= new Date())
    ) {
      return true;
    }

    return false;
  };
}

export const HasSubsGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean> => {
  const authService: SessionService = inject(SessionService);
  const notificationService: NotificationService = inject(NotificationService);
  const translateService: TranslateService = inject(TranslateService);
  const navService: CustomNavigationService = inject(CustomNavigationService);
  const subs = authService.getSubscription() as UserSubscription;
  const subscription = inject(SubscriptionService);
  const initialCheck = checkSub(
    subs,
    notificationService,
    navService,
    translateService,
  );

  console.log('initial check', initialCheck);

  if (initialCheck) {
    return of(true);
  }

  return subscription.getUserSubscription().pipe(
    tap((userSub) => authService.storeSubscription(userSub)),
    map((userSub: UserSubscription) =>
      checkSub(userSub, notificationService, navService, translateService),
    ),
    catchError(() => {
      // Handle API errors gracefully
      return of(false);
    }),
  );
};


export const hasSubscriptionMatch: CanMatchFn =
  (route: Route, segments: UrlSegment[]): Observable<boolean | UrlTree> => {

    const auth = inject(SessionService);
    const subscriptionSvc = inject(SubscriptionService);
    const notif = inject(NotificationService);
    const translate = inject(TranslateService);
    const nav = inject(CustomNavigationService);
    const router = inject(Router);

    const localSub = auth.getSubscription();
    const initialCheck = checkSub(localSub, notif, nav, translate, router);

    console.log('initial canMatch check:', initialCheck);

    // Si OK localement → passer
    if (initialCheck) {
      return of(true);
    }

    // Sinon → on appelle l'API
    console.log('Calling getUserSubscription() from canMatch...');

    return subscriptionSvc.getUserSubscription().pipe(
      tap(sub => {
        console.log('API returned subscription:', sub);
        auth.storeSubscription(sub);
      }),
      map(sub => {
        const pass = checkSub(sub, notif, nav, translate, router);
        return pass;
      })
    );
  };

function checkSub(
  subs: UserSubscription,
  notificationService: NotificationService,
  navService: CustomNavigationService,
  translateService: TranslateService,
  router?: Router
): boolean {
  if (
    subs?.user &&
    subs?.product &&
    subs?.product.ends_at &&
    new Date(subs.product.ends_at) > new Date()
  ) {
    return true;
  } else if (
    subs?.product &&
    subs?.user &&
    subs?.product.ends_at &&
    new Date(subs.product.ends_at) <= new Date()
  ) {
    notificationService.warning(
      translateService.instant('subscription.subs-end'),
      10000,
    );
    router?.parseUrl('/subscriptions');
  } else router?.parseUrl('/subscriptions');

  return false;
}
